import { useMemo, useState, type ReactElement } from 'react';
import {
  useZonesByMonth,
  type DisciplineZoneBlock,
  type ZoneSlot,
} from '@/lib/analytics';
import { zoneToColor } from '@/features/dashboard/zoneColors';
import { disciplineLabel } from '@/features/dashboard/WorkoutCard';
import type { Discipline } from '@/lib/workouts';

/**
 * Tab "Distribución de zonas por mes".
 *
 * - Selector mes (current / -1 / -2 / ...).
 * - 4 donuts SVG (uno por disciplina: swim / bike / run / strength).
 * - Comparativa "este mes vs mes anterior" como barras agrupadas debajo
 *   de cada donut.
 *
 * Las zonas vienen del backend, que las estima con `estimateZones`. Por las
 * limitaciones del estimador (proxy de una sola zona por entreno) la
 * distribución NO es fisiológicamente precisa, pero permite ver tendencias
 * mes a mes ("¿estoy entrenando más Z2 que el mes pasado?").
 */

const MONTH_OPTIONS_COUNT = 12;

export function ZonesByMonth(): ReactElement {
  const months = useMemo(buildMonthOptions, []);
  const [selected, setSelected] = useState<string>(months[0]?.value ?? '');
  const query = useZonesByMonth(selected);

  return (
    <article className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div>
          <label htmlFor="month-select" className="text-sm font-medium text-slate-700">
            Mes
          </label>
          <select
            id="month-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="ml-2 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        {query.data && (
          <p className="text-xs text-slate-500">
            Comparativa: {query.data.month} vs {query.data.previousMonth}
          </p>
        )}
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-sm text-slate-600">
          Cargando zonas…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="text-sm text-rose-700">
          No se pudieron cargar las zonas.
        </p>
      )}

      {query.data && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {query.data.byDiscipline.map((block) => (
            <DisciplineBlock key={block.discipline} block={block} />
          ))}
        </div>
      )}
    </article>
  );
}

function DisciplineBlock({ block }: { block: DisciplineZoneBlock }): ReactElement {
  const totalMin = block.zones.reduce((s, z) => s + z.minutes, 0);
  return (
    <section
      aria-label={`Zonas de ${disciplineLabel(block.discipline as Discipline)}`}
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">
          {disciplineLabel(block.discipline as Discipline)}
        </h3>
        <span className="text-xs text-slate-500">{Math.round(totalMin)} min este mes</span>
      </header>

      {totalMin === 0 ? (
        <p className="mt-3 text-xs text-slate-500">Sin entrenos de esta disciplina.</p>
      ) : (
        <div className="mt-3 flex items-center gap-4">
          <Donut zones={block.zones} />
          <Legend zones={block.zones} />
        </div>
      )}

      <ComparisonBars current={block.zones} previous={block.previousMonthZones} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Donut SVG
// ---------------------------------------------------------------------------

const DONUT_SIZE = 120;
const DONUT_R = 48;
const DONUT_INNER_R = 28;

function Donut({ zones }: { zones: ZoneSlot[] }): ReactElement {
  const total = zones.reduce((s, z) => s + z.minutes, 0);
  if (total <= 0) {
    return (
      <svg width={DONUT_SIZE} height={DONUT_SIZE} aria-hidden="true">
        <circle cx={DONUT_SIZE / 2} cy={DONUT_SIZE / 2} r={DONUT_R} fill="#f1f5f9" />
      </svg>
    );
  }
  let acc = 0;
  const cx = DONUT_SIZE / 2;
  const cy = DONUT_SIZE / 2;
  return (
    <svg width={DONUT_SIZE} height={DONUT_SIZE} role="img" aria-label="Donut de zonas">
      <circle cx={cx} cy={cy} r={DONUT_R} fill="#f8fafc" />
      {zones.map((z) => {
        const start = (acc / total) * 2 * Math.PI;
        acc += z.minutes;
        const end = (acc / total) * 2 * Math.PI;
        const d = arcPath(cx, cy, DONUT_R, DONUT_INNER_R, start, end);
        const c = zoneToColor(z.zone);
        return <path key={z.zone} d={d} fill={c.hex} stroke="#ffffff" strokeWidth={1} />;
      })}
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={11}
        fontWeight={600}
        fill="rgb(240 240 245)"
      >
        {Math.round(total)}m
      </text>
    </svg>
  );
}

function Legend({ zones }: { zones: ZoneSlot[] }): ReactElement {
  return (
    <ul className="flex-1 space-y-1 text-xs text-slate-700">
      {zones.map((z) => {
        const c = zoneToColor(z.zone);
        return (
          <li key={z.zone} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: c.hex }}
            />
            <span className="font-medium text-slate-900">{z.zone.toUpperCase()}</span>
            <span className="text-slate-500">{Math.round(z.percent)}%</span>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Barras agrupadas current vs previous
// ---------------------------------------------------------------------------

function ComparisonBars({
  current,
  previous,
}: {
  current: ZoneSlot[];
  previous: ZoneSlot[];
}): ReactElement {
  const zones = useMemo(() => {
    const all = new Set<string>();
    current.forEach((z) => all.add(z.zone));
    previous.forEach((z) => all.add(z.zone));
    return Array.from(all);
  }, [current, previous]);

  if (zones.length === 0) return <></>;
  const max = Math.max(
    1,
    ...current.map((z) => z.percent),
    ...previous.map((z) => z.percent),
  );
  const currentMap = new Map(current.map((z) => [z.zone, z.percent]));
  const previousMap = new Map(previous.map((z) => [z.zone, z.percent]));

  return (
    <div className="mt-4 border-t border-slate-100 pt-3">
      <p className="mb-2 text-[11px] uppercase tracking-wide text-slate-500">
        vs mes anterior (%)
      </p>
      <ul className="space-y-1.5">
        {zones.map((z) => {
          const curPct = currentMap.get(z) ?? 0;
          const prevPct = previousMap.get(z) ?? 0;
          const c = zoneToColor(z);
          return (
            <li key={z} className="text-xs">
              <span className="font-medium text-slate-900">{z.toUpperCase()}</span>
              <div className="mt-0.5 space-y-0.5">
                <Bar pct={curPct} max={max} color={c.hex} label={`Actual ${curPct.toFixed(0)}%`} />
                <Bar pct={prevPct} max={max} color="#94a3b8" label={`Anterior ${prevPct.toFixed(0)}%`} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Bar({
  pct,
  max,
  color,
  label,
}: {
  pct: number;
  max: number;
  color: string;
  label: string;
}): ReactElement {
  const widthPct = (pct / max) * 100;
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-2 rounded-sm"
        style={{ width: `${widthPct}%`, backgroundColor: color, minWidth: pct > 0 ? '2px' : 0 }}
        aria-hidden="true"
      />
      <span className="text-[10px] text-slate-600">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildMonthOptions(): Array<{ value: string; label: string }> {
  const now = new Date();
  const out: Array<{ value: string; label: string }> = [];
  for (let i = 0; i < MONTH_OPTIONS_COUNT; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const value = `${year}-${String(month).padStart(2, '0')}`;
    const label = new Intl.DateTimeFormat('es-ES', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(d);
    out.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return out;
}

/**
 * Path SVG para un sector de donut (arco anular). `startRad`/`endRad` en
 * radianes, 0 = arriba, sentido horario.
 */
function arcPath(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  startRad: number,
  endRad: number,
): string {
  // Convertir "0 arriba, sentido horario" a coords matemáticas estándar.
  function pt(r: number, a: number): [number, number] {
    const x = cx + r * Math.sin(a);
    const y = cy - r * Math.cos(a);
    return [x, y];
  }
  const large = endRad - startRad > Math.PI ? 1 : 0;
  const [x1, y1] = pt(rOuter, startRad);
  const [x2, y2] = pt(rOuter, endRad);
  const [x3, y3] = pt(rInner, endRad);
  const [x4, y4] = pt(rInner, startRad);
  return [
    `M${x1.toFixed(2)},${y1.toFixed(2)}`,
    `A${rOuter},${rOuter} 0 ${large} 1 ${x2.toFixed(2)},${y2.toFixed(2)}`,
    `L${x3.toFixed(2)},${y3.toFixed(2)}`,
    `A${rInner},${rInner} 0 ${large} 0 ${x4.toFixed(2)},${y4.toFixed(2)}`,
    'Z',
  ].join(' ');
}
